import React, { useState, useMemo, useRef, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  FileDiff,
  CheckCircle,
  Filter,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Upload,
  Loader,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// --- Components ---

const StatusIcon = ({ type }) => {
  if (type === "errors")
    return <AlertCircle className="w-5 h-5 text-red-500" />;
  if (type === "update")
    return <FileDiff className="w-5 h-5 text-yellow-500" />;
  if (type === "insert")
    return <CheckCircle className="w-5 h-5 text-green-500 opacity-50" />;
  return null;
};

const Cell = memo(({ value, error, update }) => {
  let className = "dark:text-gray-300";
  let content = value;
  let title = typeof value === "string" ? value : JSON.stringify(value);

  if (error) {
    className +=
      " bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200";
    title = error;
  } else if (update) {
    className +=
      " bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200";
    content = (
      <div className="flex flex-col text-xs">
        <span className="line-through opacity-60 mr-1 text-red-600 dark:text-red-400">
          {String(update.old_value)}
        </span>
        <span className="font-bold text-green-600 dark:text-green-400">
          {String(update.new_value)}
        </span>
      </div>
    );
    title = `Update: ${update.old_value} -> ${update.new_value}`;
  }

  return (
    <td
      className={`px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis flex-1 ${className}`}
      title={title}
      style={{ maxWidth: "300px", minWidth: "150px" }}
    >
      {error && (
        <AlertCircle className="w-4 h-4 text-red-500 mr-2 inline-block" />
      )}
      {content}
    </td>
  );
});

const ErrorDetailsRow = ({ errors, colSpan }) => {
  return (
    <tr className="bg-red-50 dark:bg-red-900/20">
      <td colSpan={colSpan} className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col space-y-2">
          <h4 className="font-bold text-red-700 dark:text-red-300 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-2" />
            Row Errors:
          </h4>
          <ul className="list-disc pl-5 space-y-1">
            {Object.entries(errors).map(([col, errorMsgs]) => (
              <li key={col} className="text-sm text-red-600 dark:text-red-400">
                <span className="font-semibold">{col}:</span>{" "}
                {errorMsgs.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      </td>
    </tr>
  );
};

const Row = memo(({ row, columns }) => {
  const { errors, updates, type } = row._processed;
  const [isExpanded, setIsExpanded] = useState(false);
  const hasErrors = type === "errors";

  let rowStatusClass = "border-l-4 ";
  if (type === "errors")
    rowStatusClass += "border-l-red-500 bg-red-50 dark:bg-red-900/10";
  else if (type === "update")
    rowStatusClass +=
      "border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10";
  else rowStatusClass += "border-l-green-500 bg-white dark:bg-gray-900";

  return (
    <>
      <tr className={rowStatusClass}>
        {/* Status Icon Cell */}
        <td className="border-b border-gray-200 dark:border-gray-700 w-12 relative">
            <div className="flex items-center justify-center p-2 h-full">
            {hasErrors && (
                <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="absolute left-1 top-1/2 -translate-y-1/2 p-1 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors focus:outline-none"
                >
                {isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                ) : (
                    <ChevronRight className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                )}
                </button>
            )}
            <StatusIcon type={type} />
            </div>
        </td>

        {/* ID Column Cell */}
        <td className="border-b border-r border-gray-200 dark:border-gray-700 text-gray-500 text-xs px-2 text-center w-16">
          {row.id}
        </td>

        {/* Data Cells */}
        {columns.map((col) => (
          <Cell
            key={col}
            value={row.values[col]}
            error={errors[col] ? errors[col].join("\n") : null}
            update={updates[col]}
          />
        ))}
      </tr>
      {isExpanded && hasErrors && (
        <ErrorDetailsRow errors={errors} colSpan={columns.length + 2} />
      )}
    </>
  );
});

const processRow = (row) => {
  const typeTranslations = {
    "INTEGER": "Nombre entier",
    "FLOAT": "Nombre décimal",
    "BOOLEAN": "Oui ou Non",
    "TEXT": "Texte",
    "DATE": "Date",
    "TIMESTAMP": "Date et heure, ex: '2030-12-01' ou '2030-12-01 12:00:00'",
  };
  // Check for errors
  const errors = {};
  let hasError = false;

  // Helper to add error
  const addError = (col, msg) => {
    if (!errors[col]) errors[col] = [];
    errors[col].push(msg);
    hasError = true;
  };

  // Parse errors structure
  row.errors.not_null.forEach((col) => addError(col, "Obligatoire, doit avoir une valeur"));

  // unique_within
  row.errors.unique_within.forEach((cols) => {
    const colStr = cols.join(", ")
    const colStrWithValue = cols.map((c) => `${c}=${row.values[c]}`).join(", ");
    addError(colStr, `"${colStrWithValue}" existent en double dans l'Excel`)
  });

  // unique_original
  row.errors.unique_original.forEach((err) => {
    const colStr = new Intl.ListFormat('fr-FR').format(err.unique_columns);
    const colStrWithValue = err.unique_columns.map((c) => `${c}=${row.values[c]}`).join(", ");
    addError(colStr, `"${colStrWithValue}" existent dans la table avec id=${err.original_id}`)
  });

  // foreign_key
  row.errors.foreign_key.forEach((err) => {
    const value = row.values[err.column];
    addError(
      err.column,
      `${err.column}=${value} n'existe pas dans la table "${err.foreign_table}"`
    );
  });

  // type
  row.errors.type.forEach((err) => {
    addError(err.column, `Le type de la valeur "${err.column}=${row.values[err.column]}" n'est pas valide. Attendu: ${typeTranslations[err.expected_type]}`);
  });

  // enum
  row.errors.enum.forEach((err) => {
    addError(err.column, `La valeur "${err.column}=${row.values[err.column]}" n'est pas valide. Attendu: ${err.expected_values.join(", ")}`);
  });

  // Check for updates
  const updates = {};
  let hasUpdate = false;
  row.updated_values.forEach((upd) => {
    updates[upd.column] = upd;
    hasUpdate = true;
  });
  

  let type = "insert";
  if (hasError) type = "errors";
  else if (hasUpdate) type = "update";

  return {
    ...row,
    _processed: {
      errors,
      updates,
      type,
    },
  };
};

// Result Row Component
const ResultRow = memo(({ row, columns }) => {
  const { action, values } = row;
  
  let rowStatusClass = "border-l-4 ";
  if (action === "update")
    rowStatusClass += "border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10";
  else if (action === "insert")
    rowStatusClass += "border-l-green-500 bg-white dark:bg-gray-900";
  else 
    rowStatusClass += "border-l-gray-300 bg-white dark:bg-gray-900";

  return (
    <tr className={rowStatusClass}>
      {/* Status Icon Cell */}
      <td className="border-b border-gray-200 dark:border-gray-700 w-12 relative text-center">
        {action === "update" ? (
          <FileDiff className="w-5 h-5 text-yellow-500 mx-auto" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
        )}
      </td>
      <td className="text-gray-600 dark:text-gray-200 p-4 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis font-bold capitalize">
        {action}
      </td>
      {columns.map((col) => (
        <td
          key={col}
          className="text-gray-600 dark:text-gray-200 p-4 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis"
        >
          {values[col] === null ? "NULL" : String(values[col])}
        </td>
      ))}
    </tr>
  );
});

export default function PreviewImport() {
  const { fileName } = useParams();
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  
  const importState = useSelector((state) => state.previewImport[fileName]);
  const rawData = Array.isArray(importState) ? importState : importState?.rows;
  const tableName = !Array.isArray(importState) ? importState?.tableName : null;
  
  const [selectedFilters, setSelectedFilters] = useState([
    "errors",
    "update",
    "insert",
  ]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  const columns = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    // Ensure values exists before trying to get keys
    if (!rawData[0].values) return [];
    return Object.keys(rawData[0].values);
  }, [rawData]);

  const filteredData = useMemo(() => {
    if (!rawData) return [];
    return rawData
      .map(processRow)
      .filter((row) => selectedFilters.includes(row._processed.type));
  }, [rawData, selectedFilters]);

  // For result table
  const resultColumns = useMemo(() => {
    if (!importResult || importResult.length === 0) return [];
    return Object.keys(importResult[0].values || {});
  }, [importResult]);

  const toggleFilter = (filter) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  };

  const handleImport = async () => {
    if (!rawData || rawData.length === 0 || !tableName) {
      setError("Missing data or table name for import");
      return;
    }
    
    // Get preview_id from the first row
    const previewId = rawData[0].preview_id;
    if (!previewId) {
       setError("No preview ID found. Please try uploading the file again.");
       return;
    }

    setImporting(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`/tables/${tableName}/import?preview_id=${previewId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Import failed");
      }
      
      const result = await response.json();
      setImportResult(result);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  if (!rawData) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400 ">
        No data found for {fileName}. Please try importing again.
        <br />
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-500 hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  // If import finished successfully, show results
  if (importResult) {
     return (
       <div className="flex flex-col items-center gap-5 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
         <div className="w-full p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center bg-white dark:bg-gray-900 z-20 shadow-sm">
           <div className="flex items-center mb-4 md:mb-0">
             <button
               onClick={() => navigate(-1)}
               className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
             >
               <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
             </button>
             <div>
               <h1 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                 <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                 Import Successful
               </h1>
               <p className="text-sm text-gray-500 dark:text-gray-400">
                 {importResult.length} rows processed
               </p>
             </div>
           </div>
           
           <button
             onClick={() => navigate(`/tables/${tableName}`)}
             className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
           >
             Return to Table
           </button>
         </div>
         
         <div className="overflow-auto w-[95vw] h-[80vh] scroll-bar relative">
           <table className="w-full text-left border-collapse">
             <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
               <tr>
                 <th className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm w-12 text-center">
                   Status
                 </th>
                 <th className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm">
                   Action
                 </th>
                 {resultColumns.map((col) => (
                   <th
                     key={col}
                     className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis"
                   >
                     {col}
                   </th>
                 ))}
               </tr>
             </thead>
             <tbody>
               {importResult.map((row, index) => (
                 <ResultRow
                   key={index}
                   row={row}
                   columns={resultColumns}
                 />
               ))}
             </tbody>
           </table>
         </div>
       </div>
     );
  }

  return (
    // View container
    <div className="flex flex-col items-center gap-5 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
      {/* Header Toolbar */}
      <div className="w-full p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center bg-white dark:bg-gray-900 z-20 shadow-sm">
        <div className="flex items-center mb-4 md:mb-0">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">
              Import Preview
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fileName} • {filteredData.length} rows
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
             {error && (
                <div className="text-red-500 text-sm mr-4 flex items-center">
                    <XCircle className="w-4 h-4 mr-1"/>
                    {error}
                </div>
             )}
            
            {/* Action Button */}
             <button
                onClick={handleImport}
                disabled={importing || !tableName}
                className={`px-4 py-2 rounded flex items-center shadow-sm font-medium transition-colors ${
                  importing || !tableName
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
             >
                {importing ? (
                    <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                    </>
                ) : (
                    <>
                        <Upload className="w-4 h-4 mr-2" />
                        Confirm Import
                    </>
                )}
             </button>

            {/* Filters  */}
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
            <div className="px-3 py-1 flex items-center text-sm font-medium text-gray-600 dark:text-gray-400">
                <Filter className="w-4 h-4 mr-2" />
                Filter:
            </div>
            {["errors", "update", "insert"].map((filter) => (
                <button
                key={filter}
                onClick={() => toggleFilter(filter)}
                className={`px-3 py-1 rounded-md text-sm capitalize transition-colors flex items-center ${
                    selectedFilters.includes(filter)
                    ? filter === "errors"
                        ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200"
                        : filter === "update"
                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-200"
                        : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200"
                    : "text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
                >
                {filter === "errors" && <AlertCircle className="w-3 h-3 mr-1" />}
                {filter === "update" && <FileDiff className="w-3 h-3 mr-1" />}
                {filter === "insert" && <CheckCircle className="w-3 h-3 mr-1" />}
                {filter}
                </button>
            ))}
            </div>
        </div>
      </div>

      {/* Table container */}
      <div
        className="overflow-auto w-[95vw] h-[80vh] scroll-bar relative"
      >
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
            <tr>
              <th className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis">
                Status
              </th>
              <th className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis">
                Excel Line
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="p-4 text-gray-800 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 text-sm whitespace-nowrap text-ellipsis"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row) => (
              <Row
                key={row.id}
                row={row}
                columns={columns}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
